/**
 * Dependencies.
 */
import { isPlainObject } from 'lodash-es';
import mqtt, {
  IClientPublishOptions,
  IClientSubscribeOptions,
  MqttClient
} from 'mqtt';

import { MqttPayloadModel } from '@/modules/main/models/mqtt-payload.model';

/**
 * Constant `INITIAL_RETRY_DELAY`.
 */
const INITIAL_RETRY_DELAY = 1000;

/**
 * Constant `MAX_RETRY_DELAY`.
 */
const MAX_RETRY_DELAY = 60000;

/**
 * Function `getNextRetryDelay`.
 */
function getNextRetryDelay(
  currentDelay: number,
  maxDelay: number,
  initialDelay: number
) {
  return currentDelay > 0 ? Math.min(currentDelay * 2, maxDelay) : initialDelay;
}

/**
 * `MqttService`, please read the file `README-MQTT.md` in project root.
 */
class MqttService {
  /**
   * Property `onMessageArrivedCallback`.
   */
  onMessageArrivedCallback: ({
    payload,
    topic
  }: {
    payload: MqttPayloadModel;
    topic: string;
  }) => void = null;

  /**
   * Property `client`.
   */
  private client: MqttClient | undefined = undefined;

  /**
   *  Property `currentRetryDelay`.
   */
  private currentRetryDelay = 0;

  /**
   * Method `connect`.
   */
  connect(clientId: string) {
    try {
      if (this.client) {
        return;
      }

      const finalUrl = new URL(process.env.MQTT_URL, window.location.origin);
      finalUrl.searchParams.append(
        'referer',
        window.location.origin +
          window.location.pathname +
          window.location.search
      );
      finalUrl.searchParams.append('clientId', clientId);

      this.client = mqtt.connect(finalUrl.toString(), {
        clean: true,
        clientId,
        connectTimeout: 50 * 1000,
        keepalive: 50, // Needs to be lower than 60 because of the nginx proxy_read_timeout
        password: process.env.MQTT_PASSWORD,
        reconnectPeriod: INITIAL_RETRY_DELAY,
        resubscribe: true,
        transformWsUrl: (url, options) => {
          // Incremental retry.
          this.currentRetryDelay = getNextRetryDelay(
            this.currentRetryDelay,
            MAX_RETRY_DELAY,
            INITIAL_RETRY_DELAY
          );

          options.reconnectPeriod = this.currentRetryDelay;

          return url;
        },
        username: process.env.MQTT_USERNAME
      });

      this.client.on('connect', () => {
        console.info('Connected to mqtt');

        this.currentRetryDelay = 0;
      });

      this.client.on('error', error => {
        console.error(
          `Error connecting to mqtt: ${
            isPlainObject(error) ? JSON.stringify(error) : error
          }`
        );
      });

      this.client.on('message', (topic, message) => {
        try {
          const messageString = message.toString();

          const payload: MqttPayloadModel = JSON.parse(messageString);

          if (this.onMessageArrivedCallback) {
            this.onMessageArrivedCallback({ payload, topic });
          }
        } catch (error) {
          console.error(
            `Error parsing received mqtt message: ${
              isPlainObject(error) ? JSON.stringify(error) : error
            }`
          );
        }
      });
    } catch (error) {
      console.error(
        `Error creating mqtt client: ${
          isPlainObject(error) ? JSON.stringify(error) : error
        }`
      );
    }
  }

  /**
   * Method `disconnect`.
   */
  disconnect() {
    this.client?.end();
  }

  /**
   * Method `subscribe`.
   */
  subscribe(topic: string | string[], opts?: IClientSubscribeOptions) {
    this.client?.subscribe(topic, { qos: 2, ...opts });
  }

  /**
   * Method `unsubscribe`.
   */
  unsubscribe(topic: string | string[]) {
    this.client?.unsubscribe(topic);
  }

  /**
   * Method publish.
   */
  publish({
    opts,
    payload,
    topic
  }: {
    opts?: IClientPublishOptions;
    payload: any;
    topic: string;
  }) {
    try {
      const payloadString = JSON.stringify(payload);

      this.client?.publish(topic, payloadString, {
        qos: 2,
        retain: false,
        ...opts
      });
    } catch (error) {
      console.error(
        `Error publishing mqtt message: ${
          isPlainObject(error) ? JSON.stringify(error) : error
        }`
      );
    }
  }
}

/**
 * Export `mqttService`.
 */
export const mqttService = new MqttService();
